import { type Editor, Extension } from '@tiptap/core';
import { useSyncExternalStore } from 'react';

type ComposerDraftContextStorage = { getDraftId: () => string | undefined };

declare module '@tiptap/core' {
  interface Storage {
    composerDraftContext?: ComposerDraftContextStorage;
  }
}

const composerEditors = new Set<Editor>();
const registryListeners = new Set<() => void>();

const notifyRegistryChanged = () =>
  registryListeners.forEach((listener) => listener());

const subscribeToRegistry = (listener: () => void) => {
  registryListeners.add(listener);
  return () => {
    registryListeners.delete(listener);
  };
};

export const ComposerDraftContext = Extension.create<
  ComposerDraftContextStorage,
  ComposerDraftContextStorage
>({
  name: 'composerDraftContext',
  addOptions: () => ({ getDraftId: () => undefined }),
  addStorage() {
    return { getDraftId: this.options.getDraftId };
  },
  onCreate() {
    composerEditors.add(this.editor);
    notifyRegistryChanged();
  },
  onDestroy() {
    composerEditors.delete(this.editor);
    notifyRegistryChanged();
  },
});

const composerDraftIdOf = (editor: Editor | undefined) =>
  editor?.storage.composerDraftContext?.getDraftId();

export const composerEditorOf = (draftId: string) =>
  [...composerEditors].find((editor) => composerDraftIdOf(editor) === draftId);

/** The editor of the draft, which registers after the composer has rendered. A read of `composerEditorOf` during render
 * stays as it was, even when the editor registers later, because the React Compiler keeps the result for as long as the
 * draft id is the same. This hook renders again when the editor joins or leaves. */
export const useComposerEditorOf = (draftId: string): Editor | undefined =>
  useSyncExternalStore(subscribeToRegistry, () => composerEditorOf(draftId));
